import React from 'react';

interface SkeletonProps {
  className?: string;
  children?: React.ReactNode;
}

interface SkeletonItemProps {
  className?: string;
  height?: string;
  width?: string;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
}

// Base skeleton item
export const SkeletonItem: React.FC<SkeletonItemProps> = ({ 
  className = '', 
  height = 'h-4', 
  width = 'w-full', 
  rounded = 'md' 
}) => {
  const roundedClasses = {
    none: '',
    sm: 'rounded-sm',
    md: 'rounded',
    lg: 'rounded-lg',
    full: 'rounded-full'
  };

  return (
    <div 
      className={`bg-neutral-200 animate-pulse ${height} ${width} ${roundedClasses[rounded]} ${className}`}
      aria-hidden="true"
    />
  );
};

// Card skeleton
export const SkeletonCard: React.FC<SkeletonProps> = ({ className = '', children }) => {
  return (
    <div className={`bg-white rounded-lg shadow-md p-6 animate-pulse ${className}`}>
      {children}
    </div>
  );
};

// Table row skeleton
export const SkeletonTableRow: React.FC<{ columns: number; className?: string }> = ({ 
  columns, 
  className = '' 
}) => {
  return (
    <tr className={`animate-pulse ${className}`}>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-6 py-4">
          <SkeletonItem height="h-4" width="w-24" />
        </td>
      ))}
    </tr>
  );
};

// Property card skeleton
export const SkeletonPropertyCard: React.FC<SkeletonProps> = ({ className = '' }) => {
  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden ${className}`}>
      {/* Shimmer effect overlay */}
      <div className="relative">
        <div className="h-48 bg-neutral-200 animate-pulse" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" 
             style={{ animationDuration: '2s', animationIterationCount: 'infinite' }} />
      </div>
      <div className="p-4 space-y-3">
        <div className="space-y-2 animate-pulse">
          <SkeletonItem height="h-6" width="w-1/2" />
          <SkeletonItem height="h-5" width="w-3/4" />
          <SkeletonItem height="h-4" width="w-2/3" />
        </div>
        <div className="flex space-x-4 pt-2 animate-pulse">
          <SkeletonItem height="h-5" width="w-16" />
          <SkeletonItem height="h-5" width="w-16" />
          <SkeletonItem height="h-5" width="w-16" />
        </div>
        <div className="pt-2 space-y-2 animate-pulse">
          <SkeletonItem height="h-4" width="w-1/2" />
          <SkeletonItem height="h-8" width="w-24" />
        </div>
        <div className="pt-3 border-t border-neutral-200 animate-pulse">
          <SkeletonItem height="h-10" width="w-full" rounded="md" />
        </div>
      </div>
    </div>
  );
};

// Form skeleton
export const SkeletonForm: React.FC<{ fields: number; className?: string }> = ({ 
  fields, 
  className = '' 
}) => {
  return (
    <div className={`space-y-6 ${className}`}>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2">
          <SkeletonItem height="h-4" width="w-24" />
          <SkeletonItem height="h-10" width="w-full" />
        </div>
      ))}
      <div className="flex space-x-3 pt-4">
        <SkeletonItem height="h-10" width="w-24" />
        <SkeletonItem height="h-10" width="w-24" />
      </div>
    </div>
  );
};

// Dashboard stats skeleton
export const SkeletonDashboardStats: React.FC<{ count?: number; className?: string }> = ({ 
  count = 4, 
  className = '' 
}) => {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i}>
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              <SkeletonItem height="h-4" width="w-24" />
              <SkeletonItem height="h-8" width="w-16" />
            </div>
            <SkeletonItem height="h-12" width="w-12" rounded="lg" />
          </div>
        </SkeletonCard>
      ))}
    </div>
  );
};

// List skeleton
export const SkeletonList: React.FC<{ items: number; className?: string }> = ({ 
  items, 
  className = '' 
}) => {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="flex items-center space-x-4 p-4 bg-white rounded-lg shadow-sm">
          <SkeletonItem height="h-12" width="w-12" rounded="full" />
          <div className="flex-1 space-y-2">
            <SkeletonItem height="h-4" width="w-3/4" />
            <SkeletonItem height="h-3" width="w-1/2" />
          </div>
          <SkeletonItem height="h-8" width="w-20" />
        </div>
      ))}
    </div>
  );
};

// Default export for convenience
const Skeleton = {
  Item: SkeletonItem,
  Card: SkeletonCard,
  TableRow: SkeletonTableRow,
  PropertyCard: SkeletonPropertyCard,
  Form: SkeletonForm,
  DashboardStats: SkeletonDashboardStats,
  List: SkeletonList
};

export default Skeleton;
